import type { FC, PropsWithChildren } from 'react';
import {
  type Edge,
  MarkerType,
  type Node,
  ReactFlow,
  type ReactFlowProps,
  ReactFlowProvider,
} from 'reactflow';
import { ArrowEdge } from './ArrowEdge';
import { ArrowEdgeFlowZIndexes } from './constants';
/**
 * It is recommended on the library side to define this outside the component.
 * @see https://reactflow.dev/learn/troubleshooting#002
 */
const edgeTypes = { customEdge: ArrowEdge };

type Props = {
  nodes: Node[];
  edges: Edge[];
} & Pick<
  ReactFlowProps,
  'nodeTypes' | 'onNodesChange' | 'onEdgesChange' | 'attributionPosition'
>;

export const ArrowEdgeFlow: FC<PropsWithChildren<Props>> = ({
  children,
  nodes,
  edges,
  nodeTypes,
  onNodesChange,
  onEdgesChange,
  attributionPosition,
}) => {
  const customTypeAppliedEdges = edges.map((edge) => ({
    type: 'customEdge',
    zIndex: ArrowEdgeFlowZIndexes.edge,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: edge.data?.variant === 'secondary' ? '#999' : '#000',
      width: 20,
      height: 20,
    },
    ...edge,
  }));

  const zIndexAppliedNodes = nodes.map((node) => ({
    zIndex: ArrowEdgeFlowZIndexes.node,
    ...node,
  }));

  return (
    <ReactFlowProvider>
      <ReactFlow
        nodes={zIndexAppliedNodes}
        edges={customTypeAppliedEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        deleteKeyCode={null}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        attributionPosition={attributionPosition}
        proOptions={{ hideAttribution: true }}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        zoomOnScroll={false}
        minZoom={0.01}
        preventScrolling={false}
      >
        {children}
      </ReactFlow>
    </ReactFlowProvider>
  );
};
